/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { useCallback, useLayoutEffect, useRef } from 'react';

import { FILTERED_GUIDE_TIMEOUT } from '../../../../config';
import {
    RootState,
    useTivoDispatch,
    useTivoSelector,
} from '../../../../core/tivo/tivo-redux-wrapper';
import { updateFilteredGuideLastRefetchTimestampAction } from '../../../../store/guide/filteredguide/filteredguide.actions';
import { resetCategoriesFilterAction } from '../../../../store/overlay/channeloptionsoverlay/channeloptionsoverlay.actions';
import { showGuideScreen } from '../../../../store/screens/screens.reducers';
import { useUpdateEffect } from '../../../hooks';

export const useFilteredGuideTimeout = () => {
    const { lastRefetchTimestamp, remountCounter } = useTivoSelector(
        (state: RootState) => state.filteredGuide,
    );
    const { isActive: isAntiburnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const channelOptionsOverlay = useTivoSelector(
        (state: RootState) => state.channelOptionsOverlay,
    );
    const dispatch = useTivoDispatch();
    const antiburnWasEnabledRef = useRef<boolean>(false);

    const checkForTimeoutSession = useCallback(
        (lastRefetchTimestampProp: number | null) => {
            if (!lastRefetchTimestampProp) {
                dispatch(updateFilteredGuideLastRefetchTimestampAction());
                return;
            }
            const timestampNow = new Date().getTime();
            const timeSinceLastRefetch = timestampNow - lastRefetchTimestampProp;
            const isPastTimeout = timeSinceLastRefetch >= FILTERED_GUIDE_TIMEOUT;

            if (isPastTimeout) {
                dispatch(resetCategoriesFilterAction());
                dispatch(showGuideScreen());
            } else {
                dispatch(updateFilteredGuideLastRefetchTimestampAction());
            }
        },
        [dispatch],
    );

    useLayoutEffect(() => {
        checkForTimeoutSession(lastRefetchTimestamp);
    }, []);

    useUpdateEffect(() => {
        if (isAntiburnOsdActive) {
            antiburnWasEnabledRef.current = true;
        } else if (antiburnWasEnabledRef.current && !isAntiburnOsdActive) {
            antiburnWasEnabledRef.current = false;
            checkForTimeoutSession(lastRefetchTimestamp);
        }
    }, [isAntiburnOsdActive]);

    useUpdateEffect(() => {
        dispatch(updateFilteredGuideLastRefetchTimestampAction());
    }, [
        channelOptionsOverlay.channelsType,
        channelOptionsOverlay.categoriesType,
        remountCounter.toString(),
    ]);
};
